import React, { useMemo } from 'react';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Tooltip, Legend, Filler,
} from 'chart.js';
import { useTheme, Box, Typography } from '@mui/material';
import dayjs from 'dayjs';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Tooltip, Legend, Filler);

// Shared, theme-aware chart options — mirrors the dashboard chart styling so the
// whole platform reads as one system (recessive grid/axes, single dark tooltip).
const baseOpts = (theme) => {
  const grid = theme.palette.divider;
  const tick = theme.palette.text.secondary;
  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: theme.palette.mode === 'dark' ? '#1e293b' : '#0f172a',
        titleColor: '#fff', bodyColor: '#e2e8f0', padding: 10, cornerRadius: 8, displayColors: false,
      },
    },
    scales: {
      x: { grid: { display: false }, border: { color: grid }, ticks: { color: tick, font: { size: 11 } } },
      y: { grid: { color: grid, drawBorder: false }, border: { display: false }, ticks: { color: tick, font: { size: 11 }, precision: 0, maxTicksLimit: 5 }, beginAtZero: true },
    },
  };
};

const EmptyState = ({ label = 'No data in this range' }) => (
  <Box sx={{ height: '100%', display: 'grid', placeItems: 'center' }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
  </Box>
);

// Commands per day — single series, brand hue, gradient fill (magnitude over time).
export const ActivityOverTime = ({ series = [] }) => {
  const theme = useTheme();
  const brand = theme.palette.primary.main;
  const hasData = series.some((s) => s.count > 0);
  const data = useMemo(() => ({
    labels: series.map((s) => dayjs(s.day).format('MMM D')),
    datasets: [{
      data: series.map((s) => s.count),
      borderColor: brand,
      backgroundColor: (ctx) => {
        const { chartArea, ctx: c } = ctx.chart;
        if (!chartArea) return 'transparent';
        const g = c.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
        g.addColorStop(0, brand + '40'); g.addColorStop(1, brand + '00');
        return g;
      },
      borderWidth: 2, fill: true, tension: 0.35,
      pointRadius: 0, pointHoverRadius: 5, pointHoverBackgroundColor: brand,
    }],
  }), [series, brand]);
  const opts = useMemo(() => {
    const o = baseOpts(theme);
    o.plugins.tooltip.callbacks = { title: (i) => i[0].label, label: (i) => ` ${i.parsed.y} commands` };
    o.interaction = { mode: 'index', intersect: false };
    return o;
  }, [theme]);
  if (!hasData) return <EmptyState />;
  return <Line data={data} options={opts} />;
};

// Success vs Failed — reserved STATUS colors, always with a legend + labels.
export const OutcomeDonut = ({ success = 0, failed = 0 }) => {
  const theme = useTheme();
  const total = success + failed;
  const data = useMemo(() => ({
    labels: ['Success', 'Failed'],
    datasets: [{
      data: [success, failed],
      backgroundColor: [theme.palette.success.main, theme.palette.error.main],
      borderColor: theme.palette.background.paper,
      borderWidth: 2, hoverOffset: 4,
    }],
  }), [success, failed, theme]);
  const opts = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    cutout: '66%',
    plugins: {
      legend: { position: 'bottom', labels: { color: theme.palette.text.secondary, usePointStyle: true, pointStyle: 'circle', padding: 16, font: { size: 12 } } },
      tooltip: {
        backgroundColor: theme.palette.mode === 'dark' ? '#1e293b' : '#0f172a',
        titleColor: '#fff', bodyColor: '#e2e8f0', padding: 10, cornerRadius: 8,
        callbacks: { label: (i) => ` ${i.label}: ${i.parsed} (${total ? Math.round((i.parsed / total) * 100) : 0}%)` },
      },
    },
  }), [theme, total]);
  if (!total) return <EmptyState label="No commands with a recorded outcome" />;
  return <Doughnut data={data} options={opts} />;
};

// Generic horizontal ranking bar — single series, one hue (magnitude by category).
export const RankBar = ({ rows = [], unit = '', color }) => {
  const theme = useTheme();
  const hue = color || theme.palette.primary.main;
  const data = useMemo(() => ({
    labels: rows.map((r) => r.label),
    datasets: [{
      data: rows.map((r) => r.value),
      backgroundColor: hue,
      borderRadius: 5, borderSkipped: false, barThickness: 16,
    }],
  }), [rows, hue]);
  const opts = useMemo(() => {
    const o = baseOpts(theme);
    o.indexAxis = 'y';
    o.scales.x.grid = { color: theme.palette.divider, drawBorder: false };
    o.scales.x.ticks.precision = 0;
    o.scales.y.grid = { display: false };
    o.plugins.tooltip.callbacks = { label: (i) => ` ${i.parsed.x}${unit ? ' ' + unit : ''}` };
    return o;
  }, [theme, unit]);
  if (!rows.length) return <EmptyState />;
  return <Bar data={data} options={opts} />;
};
